import React, { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { auditLogsAPI, QUERY_KEYS } from '@/api';
import { Card, CardContent, Button, Input, Badge } from '@/components/ui';
import { LoadingSpinner, Pagination } from '@/components/common';
import { History, User, Clock, ChevronDown, ChevronUp } from 'lucide-react';
import { extractPaginatedData } from '@/utils/pagination';

const ACTION_COLORS: Record<string, string> = {
  create: 'bg-green-100 text-green-800',
  update: 'bg-blue-100 text-blue-800',
  delete: 'bg-red-100 text-red-800',
  approve: 'bg-purple-100 text-purple-800',
  reject: 'bg-orange-100 text-orange-800',
  override: 'bg-yellow-100 text-yellow-800',
  refund: 'bg-emerald-100 text-emerald-800',
  recalculate: 'bg-indigo-100 text-indigo-800',
  create_adjustment: 'bg-fuchsia-100 text-fuchsia-800',
  login: 'bg-cyan-100 text-cyan-800',
  logout: 'bg-gray-100 text-gray-800',
  post: 'bg-sky-100 text-sky-800',
  patch: 'bg-violet-100 text-violet-800',
  put: 'bg-blue-100 text-blue-800',
};

export default function AuditLogsPage() {
  const [page, setPage] = useState(1);
  const [filters, setFilters] = useState({
    user_id: '',
    action: '',
    model_type: '',
    model_id: '',
    date_from: '',
    date_to: '',
  });
  const [expandedLog, setExpandedLog] = useState<number | null>(null);

  const updateFilter = (key: string, value: string) => {
    setPage(1);
    setFilters((prev) => ({ ...prev, [key]: value }));
  };

  const { data: logsData, isLoading, refetch } = useQuery({
    queryKey: [...QUERY_KEYS.auditLogs(), filters, page],
    queryFn: () => auditLogsAPI.list({ ...filters, page }),
  });

  const { items: logs, meta } = extractPaginatedData<any>(logsData, page, 20);

  const getActionBadge = (action: string) => (
    <span className={`px-2 py-1 rounded text-xs font-medium ${ACTION_COLORS[action] || 'bg-gray-100 text-gray-800'}`}>
      {action}
    </span>
  );

  if (isLoading) {
    return <LoadingSpinner />;
  }

  return (
    <div className="space-y-6 page-shell">
      <div className="page-toolbar">
        <h1 className="text-3xl font-bold">Журнал аудита</h1>
        <div className="flex gap-2">
          <Button variant="outline" onClick={() => refetch()}>Обновить</Button>
        </div>
      </div>

      <Card>
        <CardContent className="py-4">
          <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
            <div>
              <label className="text-sm text-gray-500 block mb-1">Действие</label>
              <select
                className="w-full px-4 py-2 border rounded-lg"
                value={filters.action}
                onChange={(e) => updateFilter('action', e.target.value)}
              >
                <option value="">Все действия</option>
                <option value="create">Создание</option>
                <option value="update">Обновление</option>
                <option value="delete">Удаление</option>
                <option value="approve">Подтверждение</option>
                <option value="reject">Отклонение</option>
                <option value="override">Изменение</option>
                <option value="refund">Возврат</option>
                <option value="recalculate">Перерасчет</option>
                <option value="create_adjustment">Создание корректировки</option>
                <option value="login">Вход</option>
                <option value="logout">Выход</option>
                <option value="post">POST</option>
                <option value="patch">PATCH</option>
                <option value="put">PUT</option>
              </select>
            </div>
            <div>
              <label className="text-sm text-gray-500 block mb-1">ID пользователя</label>
              <Input
                placeholder="Например: 1"
                value={filters.user_id}
                onChange={(e) => updateFilter('user_id', e.target.value)}
              />
            </div>
            <div>
              <label className="text-sm text-gray-500 block mb-1">Модель</label>
              <Input
                placeholder="Penalty, Payslip, API_REQUEST"
                value={filters.model_type}
                onChange={(e) => updateFilter('model_type', e.target.value)}
              />
            </div>
            <div>
              <label className="text-sm text-gray-500 block mb-1">ID объекта</label>
              <Input
                placeholder="Например: 15"
                value={filters.model_id}
                onChange={(e) => updateFilter('model_id', e.target.value)}
              />
            </div>
            <div>
              <label className="text-sm text-gray-500 block mb-1">Дата с</label>
              <Input
                type="date"
                value={filters.date_from}
                onChange={(e) => updateFilter('date_from', e.target.value)}
              />
            </div>
            <div>
              <label className="text-sm text-gray-500 block mb-1">Дата по</label>
              <Input
                type="date"
                value={filters.date_to}
                onChange={(e) => updateFilter('date_to', e.target.value)}
              />
            </div>
          </div>
        </CardContent>
      </Card>

      <div className="space-y-3">
        {logs.map((log: any) => (
          <Card key={log.id} className="cursor-pointer hover:shadow-md transition">
            <CardContent className="p-4">
              <div
                className="flex items-center justify-between"
                onClick={() => setExpandedLog(expandedLog === log.id ? null : log.id)}
              >
                <div className="flex items-center gap-4">
                  <div className="w-10 h-10 bg-gray-100 rounded-full flex items-center justify-center">
                    <User className="w-5 h-5 text-gray-600" />
                  </div>
                  <div>
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="font-medium">{log.user?.name || 'System'}</span>
                      {getActionBadge(log.action)}
                      <span className="text-gray-600">{log.model_type || '-'}</span>
                    </div>
                    <div className="flex items-center gap-2 text-sm text-gray-500 mt-1 flex-wrap">
                      <Clock className="w-4 h-4" />
                      {new Date(log.created_at).toLocaleString('ru-RU')}
                      {log.ip_address ? <span>IP: {log.ip_address}</span> : null}
                    </div>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  {log.model_id ? <Badge variant="secondary">ID: {log.model_id}</Badge> : null}
                  {log.new_values?.path ? <Badge variant="secondary">{log.new_values.path}</Badge> : null}
                  {expandedLog === log.id ? (
                    <ChevronUp className="w-5 h-5 text-gray-400" />
                  ) : (
                    <ChevronDown className="w-5 h-5 text-gray-400" />
                  )}
                </div>
              </div>

              {expandedLog === log.id ? (
                <div className="mt-4 pt-4 border-t">
                  <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                    {log.old_values && Object.keys(log.old_values).length > 0 ? (
                      <div>
                        <p className="text-sm font-medium text-gray-500 mb-2">Старые значения</p>
                        <pre className="bg-red-50 p-3 rounded text-xs overflow-auto max-h-64 whitespace-pre-wrap">
                          {JSON.stringify(log.old_values, null, 2)}
                        </pre>
                      </div>
                    ) : null}
                    {log.new_values && Object.keys(log.new_values).length > 0 ? (
                      <div>
                        <p className="text-sm font-medium text-gray-500 mb-2">Новые значения</p>
                        <pre className="bg-green-50 p-3 rounded text-xs overflow-auto max-h-64 whitespace-pre-wrap">
                          {JSON.stringify(log.new_values, null, 2)}
                        </pre>
                      </div>
                    ) : null}
                  </div>
                  {log.user_agent ? (
                    <div className="mt-3">
                      <p className="text-sm text-gray-500 break-all">User Agent: {log.user_agent}</p>
                    </div>
                  ) : null}
                </div>
              ) : null}
            </CardContent>
          </Card>
        ))}
      </div>

      {logs.length === 0 ? (
        <div className="text-center py-12">
          <History className="w-16 h-16 mx-auto text-gray-400 mb-4" />
          <h3 className="text-lg font-medium text-gray-900">Записи аудита не найдены</h3>
          <p className="text-gray-500">Здесь появятся действия пользователей и системные изменения.</p>
        </div>
      ) : null}

      <Pagination meta={meta} onPageChange={setPage} />
    </div>
  );
}


